import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { FORMS } from "@/config/forms";
import { PageHeader } from "@/components/common/PageHeader";
import { DetailDrawer } from "@/components/common/DetailDrawer";
import { ProductLabels } from "@/components/products/ProductLabels";
import { SellingPriceDetails } from "@/components/products/SellingPriceDetails";
import { EntityFormDialog } from "@/components/common/EntityFormDialog";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { toast } from "sonner";
import {
  Plus, Search, Eye, Pencil, Trash2, ChevronDown, ShieldCheck,
  BadgeIndianRupee, Settings2, Calculator, Download, ArrowRight,
  AlertTriangle, Package, Loader2, FileDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "bakery", label: "Bakery" },
  { key: "non_bakery", label: "Non-Bakery" },
  { key: "active", label: "Active" },
];

const isBakery = (p) => String(p.productType || "").toUpperCase() === "BAKERY";

function matchesSearch(p, q) {
  if (!q) return true;
  const query = q.trim().toLowerCase();
  const pid = String(p.productId || "").toLowerCase();
  if (pid.includes(query)) return true;
  // numeric suffix: "45" or "00045" matches PROD-000045
  const numeric = query.replace(/\D/g, "");
  if (numeric) {
    const suffix = pid.replace(/^prod-0*/, "");
    if (suffix === String(Number(numeric))) return true;
  }
  if (String(p.name || "").toLowerCase().includes(query)) return true;
  if (String(p.sku || "").toLowerCase().includes(query)) return true;
  return false;
}

function RecipeCell({ p }) {
  if (!p.hasRecipe || !p.currentRecipeId) {
    return (
      <span data-testid={`recipe-none-${p.productId}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-200">
        <AlertTriangle className="h-3 w-3" /> No recipe
      </span>
    );
  }
  const ver = String(p.currentRecipeVersion || "").replace(/^V/i, "");
  const approved = p.currentRecipeStatus === "ACTIVE" && !p.pendingRecipeApproval;
  return (
    <div data-testid={`recipe-cell-${p.productId}`} className="flex flex-col gap-0.5">
      <span className="font-mono text-xs font-semibold text-stone-700">{p.currentRecipeId}</span>
      <span
        className={cn(
          "inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
          approved
            ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
            : "bg-amber-50 text-amber-700 ring-amber-200"
        )}
      >
        v{ver} · {approved ? "Approved" : "Pending"}
      </span>
    </div>
  );
}

function CostCell({ p }) {
  const current = Number(p.currentUnitCost) || Number(p.costPrice) || 0;
  const decided = Number(p.decidedCost) || 0;
  const variancePct = Number(p.costVariancePct) || 0;
  if (!current && !decided) {
    return (
      <span data-testid={`cost-none-${p.productId}`} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200">
        <AlertTriangle className="h-3 w-3" /> No cost
      </span>
    );
  }
  const notReady = p.costStatus === "NOT_READY";
  const varianceFlag = decided > 0 && Math.abs(variancePct) > 0.1;
  return (
    <div data-testid={`cost-cell-${p.productId}`} className="flex flex-col gap-0.5">
      <span className="text-sm font-semibold text-stone-800">{fmtCurrency(current)}</span>
      <div className="flex flex-wrap items-center gap-1">
        {decided > 0 && <span className="text-[11px] text-stone-500">decided {fmtCurrency(decided)}</span>}
        {notReady && (
          <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200">not ready</span>
        )}
        {varianceFlag && (
          <span className={cn(
            "rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1",
            variancePct > 0 ? "bg-rose-50 text-rose-700 ring-rose-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"
          )}>
            {variancePct > 0 ? "+" : ""}{fmtNumber(variancePct, 1)}%
          </span>
        )}
      </div>
    </div>
  );
}

function ToolRow({ icon: Icon, title, testId, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testId} className="rounded-xl border bg-white">
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
      >
        <Icon className="h-4 w-4 text-stone-400" />
        <span className="text-xs font-bold uppercase tracking-wider text-stone-500">{title}</span>
        <ChevronDown className={cn("ml-auto h-4 w-4 text-stone-400 transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="border-t px-4 py-3">{children}</div>}
    </div>
  );
}

function IntegrityTool() {
  const { data, isLoading } = useResource("/integrity/report");
  if (isLoading) return <p className="text-xs text-stone-500">Loading integrity report…</p>;
  const s = data?.summary || {};
  const issues = data?.issues || {};
  const issueCount = Object.values(issues).reduce((n, v) => n + (Array.isArray(v) ? v.length : 0), 0);
  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-stone-600" data-testid="integrity-preview">
        <span>Ingredients: <b>{s.ingredients?.total ?? "—"}</b> ({s.ingredients?.active ?? 0} active)</span>
        <span>Products: <b>{s.products?.total ?? "—"}</b> ({s.products?.bakery ?? 0} bakery / {s.products?.nonBakery ?? 0} non-bakery)</span>
        <span>Recipes: <b>{s.recipes?.documents ?? "—"}</b> ({s.recipes?.active ?? 0} active)</span>
        <span className={cn(issueCount > 0 ? "text-amber-700" : "text-emerald-700")}>
          Open findings: <b>{issueCount}</b>
        </span>
      </div>
      <Link to="/reports" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-800" data-testid="integrity-open-link">
        Open full report <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function BuyerPricesTool() {
  const { data, isLoading } = useResource("/buyer-prices");
  if (isLoading) return <p className="text-xs text-stone-500">Loading buyer price master…</p>;
  const rows = Array.isArray(data) ? data : [];
  return (
    <div className="space-y-2 text-sm" data-testid="buyer-prices-preview">
      <p className="text-xs text-stone-600"><b>{rows.length}</b> buyer-specific prices on file</p>
      {rows.slice(0, 3).map((r) => (
        <div key={r.id} className="flex items-center gap-3 text-xs text-stone-600">
          <span className="font-mono font-semibold text-stone-700">{r.productId}</span>
          <span>{r.buyerName}</span>
          <span className="ml-auto font-semibold">{fmtCurrency(r.sellingPrice)}{r.unit ? `/${r.unit}` : ""}</span>
        </div>
      ))}
      <Link to="/buyer-prices" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-800" data-testid="buyer-prices-open-link">
        Open buyer price master <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function CostSettingsTool() {
  return (
    <div className="space-y-2 text-sm" data-testid="cost-settings-preview">
      <p className="text-xs text-stone-600">
        Cost thresholds, tolerance % and price behaviour are managed centrally on the
        Costing &amp; Variance page, computed by the shared backend.
      </p>
      <Link to="/costing" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-800" data-testid="cost-settings-open-link">
        Open cost settings <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function CostingReportsTool() {
  const { data, isLoading } = useResource("/costing-reports/summary");
  const [exporting, setExporting] = useState(false);
  if (isLoading) return <p className="text-xs text-stone-500">Loading costing summary…</p>;
  const rows = Array.isArray(data?.currentCost) ? data.currentCost : [];
  const ready = rows.filter((r) => r.costStatus === "OK").length;
  const notReady = rows.filter((r) => r.costStatus !== "OK").length;
  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/costing-reports/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "costing-report.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Costing report downloaded");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Export failed"));
    } finally {
      setExporting(false);
    }
  };
  return (
    <div className="space-y-2 text-sm" data-testid="costing-reports-preview">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-stone-600">
        <span>Products costed: <b>{rows.length}</b></span>
        <span className="text-emerald-700">Cost OK: <b>{ready}</b></span>
        <span className="text-amber-700">Not ready: <b>{notReady}</b></span>
      </div>
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={doExport} disabled={exporting} data-testid="costing-export-btn">
          <Download className="h-3 w-3" /> Export
        </Button>
        <Link to="/costing" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-800" data-testid="costing-open-link">
          Open costing &amp; variance <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

export default function Products() {
  const { role } = useAuth();
  const queryClient = useQueryClient();
  const canDelete = ["super_admin", "management"].includes(role);
  const canManageLabels = ["super_admin", "management"].includes(role);
  const [bulkPdfBusy, setBulkPdfBusy] = useState(false);
  const formCfg = FORMS["/products"];

  const { data, isLoading, isError, error, refetch } = useResource("/products");
  const { data: dash } = useResource("/products/dashboard");
  const { data: priceInsights } = useResource("/reports/product-price-insights");
  const insights = priceInsights?.products || {};
  const [priceFor, setPriceFor] = useState(null);

  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const products = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const counts = useMemo(() => ({
    all: products.length,
    bakery: products.filter(isBakery).length,
    non_bakery: products.filter((p) => !isBakery(p) && String(p.productType || "").toUpperCase() === "NON_BAKERY").length,
    active: products.filter((p) => p.active).length,
  }), [products]);

  const rows = useMemo(() => {
    let r = products;
    if (filter === "bakery") r = r.filter(isBakery);
    if (filter === "non_bakery") r = r.filter((p) => String(p.productType || "").toUpperCase() === "NON_BAKERY");
    if (filter === "active") r = r.filter((p) => p.active);
    return r.filter((p) => matchesSearch(p, query));
  }, [products, filter, query]);

  const cards = [
    { key: "total", label: "Total", value: dash?.total },
    { key: "bakery", label: "Bakery", value: dash?.bakery },
    { key: "nonBakery", label: "Non-Bakery", value: dash?.nonBakery },
    { key: "active", label: "Active", value: dash?.active },
    { key: "withoutRecipe", label: "No recipe", value: dash?.withoutRecipe, warn: true },
    { key: "pendingRecipeApproval", label: "Pending recipe", value: dash?.pendingRecipeApproval, warn: true },
    { key: "missingSellingPrice", label: "No selling price", value: dash?.missingSellingPrice, warn: true },
    { key: "missingCost", label: "No cost", value: dash?.missingCost, warn: true },
  ];

  const openCreate = () => { setFormMode("create"); setEditRow(null); setFormOpen(true); };
  const openEdit = (row) => { setFormMode("edit"); setEditRow(row); setDrawerOpen(false); setFormOpen(true); };
  const openView = (row) => { setSelected(row); setDrawerOpen(true); };

  const drawerProduct = selected ? (products.find((x) => x.id === selected.id) || selected) : null;

  const downloadBulkPdf = async () => {
    setBulkPdfBusy(true);
    try {
      const res = await api.get("/products/master-data-pdf/bulk", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "product-master-data.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Master Data PDF downloaded");
    } catch (e) {
      toast.error(apiErrorMessage(e, "PDF download failed"));
    } finally {
      setBulkPdfBusy(false);
    }
  };

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ["resource", "/products"] });
    queryClient.invalidateQueries({ queryKey: ["resource", "/products/dashboard"] });
  };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (formMode === "edit") {
        await api.put(`/products/${editRow.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setFormOpen(false);
      refetchAll();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Save failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/products/${deleteRow.id}`);
      toast.success(`${deleteRow.productId} deleted`);
      setDeleteRow(null);
      refetchAll();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <PageHeader title="PRODUCTS" subtitle="Finished-product master · permanent Product IDs">
        <Button data-testid="btn-master-data-pdf" variant="outline" className="gap-2" disabled={bulkPdfBusy} onClick={downloadBulkPdf}>
          {bulkPdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Master Data PDF
        </Button>
        <Button data-testid="btn-new-product" onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800">
          <Plus className="h-4 w-4" /> New Product
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" data-testid="products-summary-cards">
        {cards.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-xl border bg-white px-3 py-3">
            <div className={cn(
              "text-xl font-bold tabular-nums",
              c.warn && Number(c.value) > 0 ? "text-amber-700" : "text-stone-900"
            )}>
              {c.value ?? "—"}
            </div>
            <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <ToolRow icon={ShieldCheck} title="Master-Data Integrity Report" testId="tool-integrity">
          <IntegrityTool />
        </ToolRow>
        <ToolRow icon={BadgeIndianRupee} title="Buyer Price Master" testId="tool-buyer-prices">
          <BuyerPricesTool />
        </ToolRow>
        <ToolRow icon={Settings2} title="Cost Settings (Thresholds & Price Behaviour)" testId="tool-cost-settings">
          <CostSettingsTool />
        </ToolRow>
        <ToolRow icon={Calculator} title="Costing Reports (Cost · Margin · Variance · Batch)" testId="tool-costing-reports">
          <CostingReportsTool />
        </ToolRow>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5" data-testid="products-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-xs font-semibold ring-1 transition-colors",
                filter === f.key
                  ? "bg-stone-900 text-white ring-stone-900"
                  : "bg-white text-stone-600 ring-stone-200 hover:bg-stone-50"
              )}
            >
              {f.label} <span className="opacity-60">{counts[f.key]}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input
            data-testid="products-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Product ID (PROD-000045 or 45), name or SKU..."
            className="h-9 bg-white pl-9 text-sm"
          />
        </div>
      </div>

      {isLoading ? (
        <LoadingState label="Loading products…" />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={refetch} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No products found"
          message={products.length === 0 ? "There are no products in the shared ERP yet." : "No products match the current filter or search."}
          icon={Package}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-white" data-testid="products-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-stone-50/60 text-left">
                  {["Product ID", "Product Name", "Category", "Recipe", "Selling", "Cost", "Yield", "Pcs/Ctn", "Status", "Actions"].map((h) => (
                    <th key={h} className="whitespace-nowrap px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-stone-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {rows.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.productId}`} className="hover:bg-stone-50/50">
                    <td className="px-4 py-2.5">
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 font-mono text-xs font-semibold text-amber-800 ring-1 ring-amber-200/60">
                        {p.productId}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-stone-800">{p.name}</td>
                    <td className="px-4 py-2.5">
                      <span className={cn(
                        "rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
                        isBakery(p) ? "bg-amber-50 text-amber-700 ring-amber-200" : "bg-sky-50 text-sky-700 ring-sky-200"
                      )}>
                        {isBakery(p) ? "Bakery" : "Non-Bakery"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5"><RecipeCell p={p} /></td>
                    <td className="px-4 py-2.5">
                      <button data-testid={`selling-cell-${p.productId}`} className="text-left" onClick={() => setPriceFor(p)}
                        title="Invoice-derived selling price details">
                        {Number(p.sellingPrice) > 0 ? (
                          <span className="block">
                            <span className="font-semibold text-stone-800 underline decoration-dotted decoration-stone-300 underline-offset-2">{fmtCurrency(p.sellingPrice)}</span>
                            {insights[p.productId]?.overall && (
                              <span className="block text-[10px] text-stone-500">inv avg {fmtCurrency(insights[p.productId].overall.weightedAvg)}/{insights[p.productId].unit}</span>
                            )}
                          </span>
                        ) : insights[p.productId]?.overall ? (
                          <span className="block">
                            <span className="font-semibold text-stone-800 underline decoration-dotted decoration-stone-300 underline-offset-2">{fmtCurrency(insights[p.productId].overall.weightedAvg)}<span className="text-[10px] font-normal text-stone-500">/{insights[p.productId].unit}</span></span>
                            <span className="block text-[10px] text-stone-500">invoice avg · latest {fmtCurrency(insights[p.productId].overall.latest?.rate)}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200">
                            <AlertTriangle className="h-3 w-3" /> No invoice price available
                          </span>
                        )}
                        {insights[p.productId]?.multipleBuyerPrices && (
                          <span data-testid={`multi-price-${p.productId}`} className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 text-[9px] font-semibold text-sky-700 ring-1 ring-sky-200">
                            Multiple buyer prices
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-2.5"><CostCell p={p} /></td>
                    <td className="px-4 py-2.5 text-stone-600">
                      {p.standardYield ? `${fmtNumber(p.standardYield, 0)}${p.yieldUnit ? ` ${p.yieldUnit}` : ""}` : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-stone-600">{p.piecesPerCarton ? fmtNumber(p.piecesPerCarton, 0) : "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={cn(
                        "rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
                        p.active ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-stone-100 text-stone-500 ring-stone-200"
                      )}>
                        {p.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <button type="button" data-testid={`product-action-view-${p.productId}`} onClick={() => openView(p)}
                          className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700" title="View">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button type="button" data-testid={`product-action-edit-${p.productId}`} onClick={() => openEdit(p)}
                          className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        {canDelete && (
                          <button type="button" data-testid={`product-action-delete-${p.productId}`} onClick={() => setDeleteRow(p)}
                            className="rounded-md p-1.5 text-stone-400 hover:bg-rose-50 hover:text-rose-600" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t px-4 py-2 text-xs text-stone-500" data-testid="products-count">
            {rows.length} of {products.length} products
          </div>
        </div>
      )}

      <DetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={drawerProduct ? `${drawerProduct.productId} · ${drawerProduct.name}` : "Product detail"}
        row={drawerProduct}
        onEdit={drawerProduct ? () => openEdit(drawerProduct) : undefined}
      >
        {drawerProduct && (
          <ProductLabels product={drawerProduct} canManage={canManageLabels} onChanged={refetchAll} />
        )}
      </DetailDrawer>

      <EntityFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        singular="Product"
        fields={formMode === "edit" ? formCfg.editFields : formCfg.createFields}
        initialValues={formMode === "edit" ? editRow : null}
        onSubmit={handleSubmit}
        submitting={submitting}
      />

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="bg-white" data-testid="product-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteRow?.productId}?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes <b>{deleteRow?.name}</b> from the shared ERP backend (web + mobile).
              Existing recipe links and history remain untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="product-delete-confirm" onClick={handleDelete} disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700">
              {deleting ? "Deleting…" : "Delete product"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SellingPriceDetails product={priceFor} open={!!priceFor} onOpenChange={(v) => !v && setPriceFor(null)} />
    </div>
  );
}
